import {
    Box,
    Heading,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Text,
    Icon,
    HStack,
    VStack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Switch,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Divider,
    useToast,
    Center,
} from '@chakra-ui/react';
import { useState } from 'react';
import {
    MdPeople,
    MdSettings,
    MdHistory,
    MdSecurity,
    MdCloud,
    MdStorage,
} from 'react-icons/md';

export function AdminPanel() {
    const toast = useToast();
    const [settings, setSettings] = useState({
        enablePiiRedaction: true,
        enableAutoApproval: false,
        requireMfa: true,
        enableAuditLog: true,
        scraperEnabled: true,
        geminiEnabled: true,
    });

    const handleToggle = (key: keyof typeof settings) => {
        setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
        toast({
            title: 'Setting updated',
            status: 'success',
            duration: 2000,
        });
    };


    return (
        <Box>
            <Heading size="lg" mb={6}>
                Admin Panel
            </Heading>

            <Tabs colorScheme="brand" variant="enclosed">
                <TabList>
                    <Tab>
                        <Icon as={MdPeople} mr={2} />
                        Users
                    </Tab>
                    <Tab>
                        <Icon as={MdSettings} mr={2} />
                        Settings
                    </Tab>
                    <Tab>
                        <Icon as={MdHistory} mr={2} />
                        Audit Log
                    </Tab>
                    <Tab>
                        <Icon as={MdCloud} mr={2} />
                        System
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* Users Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody py={10}>
                                <Center>
                                    <VStack spacing={4}>
                                        <Icon as={MdPeople} boxSize={12} color="gray.500" />
                                        <Text color="gray.500">User management coming soon</Text>
                                    </VStack>
                                </Center>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* Settings Tab */}
                    {/* ... (Settings tab content remains as is, checking next replacement chunk) ... */}

                    {/* Audit Log Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody py={10}>
                                <Center>
                                    <VStack spacing={4}>
                                        <Icon as={MdHistory} boxSize={12} color="gray.500" />
                                        <Text color="gray.500">Audit logs coming soon</Text>
                                    </VStack>
                                </Center>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* Settings Tab */}
                    <TabPanel px={0}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <Card>
                                <CardHeader>
                                    <HStack>
                                        <Icon as={MdSecurity} color="brand.400" />
                                        <Heading size="sm">Security</Heading>
                                    </HStack>
                                </CardHeader>
                                <CardBody pt={0}>
                                    <VStack spacing={4} align="stretch">
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">PII Redaction</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Automatically detect and redact personal information
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.enablePiiRedaction}
                                                onChange={() => handleToggle('enablePiiRedaction')}
                                            />
                                        </Flex>
                                        <Divider />
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">Require MFA</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Enforce multi-factor authentication for all users
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.requireMfa}
                                                onChange={() => handleToggle('requireMfa')}
                                            />
                                        </Flex>
                                        <Divider />
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">Audit Logging</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Track all user actions for compliance
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.enableAuditLog}
                                                onChange={() => handleToggle('enableAuditLog')}
                                            />
                                        </Flex>
                                    </VStack>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <HStack>
                                        <Icon as={MdSettings} color="brand.400" />
                                        <Heading size="sm">Automation</Heading>
                                    </HStack>
                                </CardHeader>
                                <CardBody pt={0}>
                                    <VStack spacing={4} align="stretch">
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">Auto-Approval</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Auto-approve batches with high confidence scores
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.enableAutoApproval}
                                                onChange={() => handleToggle('enableAutoApproval')}
                                            />
                                        </Flex>
                                        <Divider />
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">Scrapers Enabled</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Allow scheduled scraping tasks to run
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.scraperEnabled}
                                                onChange={() => handleToggle('scraperEnabled')}
                                            />
                                        </Flex>
                                        <Divider />
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="medium">Gemini AI</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    Use AI for extraction and normalization
                                                </Text>
                                            </Box>
                                            <Switch
                                                colorScheme="brand"
                                                isChecked={settings.geminiEnabled}
                                                onChange={() => handleToggle('geminiEnabled')}
                                            />
                                        </Flex>
                                    </VStack>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    </TabPanel>

                    {/* Audit Log Tab */}
                    <TabPanel px={0}>
                        <Card>
                            <CardBody py={10}>
                                <Center>
                                    <VStack spacing={4}>
                                        <Icon as={MdHistory} boxSize={12} color="gray.500" />
                                        <Text color="gray.500">Audit logs coming soon</Text>
                                    </VStack>
                                </Center>
                            </CardBody>
                        </Card>
                    </TabPanel>

                    {/* System Tab */}
                    <TabPanel px={0}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                            <Card>
                                <CardBody>
                                    <Stat>
                                        <StatLabel color="gray.400">
                                            <HStack>
                                                <Icon as={MdStorage} />
                                                <Text>Database</Text>
                                            </HStack>
                                        </StatLabel>
                                        <StatNumber fontSize="2xl" color="green.400">Healthy</StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <Stat>
                                        <StatLabel color="gray.400">
                                            <HStack>
                                                <Icon as={MdCloud} />
                                                <Text>GCS Storage</Text>
                                            </HStack>
                                        </StatLabel>
                                        <StatNumber fontSize="2xl">2.4 GB</StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <Stat>
                                        <StatLabel color="gray.400">
                                            <HStack>
                                                <Icon as={MdSettings} />
                                                <Text>Celery Workers</Text>
                                            </HStack>
                                        </StatLabel>
                                        <StatNumber fontSize="2xl" color="green.400">3 Active</StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
